import { countFieldTime } from '../../lib/field-time-live';
import { shortName } from '../plan/DefenseGrid';

// The live game's Lineup tab: the whole game on one sheet, read-only. Innings
// already played show what actually happened (inningLogs), the inning being
// played is highlighted (the live lineup), and innings still to come show the
// plan, dimmed. Totals count innings so far, and players-pitch teams see each
// arm's pitch count.
//
// Display only. Every change to the game still happens on the Defense tab and
// in the inning flow; nothing here writes.

type Entry = { id: string; position: string };

const label = (pos: string | undefined) => (!pos ? '' : pos === 'BENCH' ? 'BN' : pos);

export const LiveLineupWall = ({
  lineup, inningLogs, currentInning, planInnings, totalInnings, pitchCounts, showPitches,
}: {
  /** Live lineup in batting order, each with its current position. */
  lineup: any[];
  /** Innings already played, keyed by inning number (1-based). */
  inningLogs: Record<number, Entry[]>;
  currentInning: number;
  /** The saved plan's innings, 0-based ("0" is inning 1). */
  planInnings?: Record<string, Entry[]>;
  totalInnings: number;
  pitchCounts: Record<string, number>;
  showPitches: boolean;
}) => {
  const lastLogged = Math.max(0, ...Object.keys(inningLogs).map(Number));
  const numInnings = Math.max(totalInnings, currentInning, lastLogged);
  const innings = Array.from({ length: numInnings }, (_, i) => i + 1);

  const cell = (inning: number, id: string): { pos: string | undefined; when: 'played' | 'now' | 'planned' } => {
    if (inning === currentInning) return { pos: lineup.find((p: any) => p.id === id)?.position, when: 'now' };
    if (inning < currentInning) return { pos: inningLogs[inning]?.find(e => e.id === id)?.position, when: 'played' };
    return { pos: planInnings?.[String(inning - 1)]?.find(e => e.id === id)?.position, when: 'planned' };
  };

  // So far: every logged inning before this one, plus the inning being played.
  const soFar: Entry[][] = [
    ...innings.filter(i => i < currentInning).map(i => inningLogs[i] || []),
    lineup.map((p: any) => ({ id: p.id, position: p.position })),
  ];
  const counts = countFieldTime(soFar, lineup.map((p: any) => p.id));

  return (
    <div className="bg-surface rounded-card border border-hairline">
      <div className="flex items-baseline justify-between px-4 pt-4 pb-2">
        <h3 className="text-sm font-semibold text-ink-2">Batting Order</h3>
        <span className="text-[12px] text-ink-2">Played, now, then the plan</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-raised">
              <th className="sticky left-0 z-10 bg-raised text-left pl-3 pr-1 py-1.5 text-[10px] font-bold uppercase tracking-[0.06em] text-ink-2">
                Player
              </th>
              {innings.map(i => (
                <th key={i} className="px-0.5 py-1">
                  <span className={`block min-w-[32px] rounded-lg py-1 text-center text-[12px] font-bold tabular-nums ${
                    i === currentInning ? 'bg-ink text-surface' : 'text-ink-2'
                  }`}>
                    {i}
                  </span>
                </th>
              ))}
              <th className="pl-2 pr-1 py-1.5 text-[10px] font-bold uppercase tracking-[0.06em] text-ink-2">Field</th>
              <th className="pl-1 pr-1 py-1.5 text-[10px] font-bold uppercase tracking-[0.06em] text-ink-2">Bench</th>
              {showPitches && (
                <th className="pl-1 pr-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.06em] text-ink-2">Pitches</th>
              )}
            </tr>
          </thead>
          <tbody>
            {lineup.map((player: any, rowIdx: number) => {
              const c = counts.get(player.id);
              const pitches = pitchCounts[player.id] || 0;
              return (
                <tr key={player.id} className={rowIdx === lineup.length - 1 ? '' : 'border-b border-hairline'}>
                  <th scope="row" className="sticky left-0 z-10 bg-surface text-left pl-3 pr-1 py-1 font-normal">
                    <span className="flex items-center gap-1.5 max-w-[110px]">
                      <span className="w-[16px] shrink-0 text-[11.5px] font-semibold tabular-nums text-ink-2">{rowIdx + 1}</span>
                      <span className="truncate text-[13.5px] font-semibold text-ink">{shortName(player.name)}</span>
                    </span>
                  </th>
                  {innings.map(i => {
                    const { pos, when } = cell(i, player.id);
                    const sitting = !pos || pos === 'BENCH' || pos === 'EH';
                    return (
                      <td key={i} className="px-0.5 py-1">
                        <span
                          aria-label={`${player.name}, inning ${i}${when === 'planned' ? ' (planned)' : ''}: ${pos === 'BENCH' ? 'Bench' : pos || 'not set'}`}
                          className={`flex items-center justify-center min-w-[32px] h-[30px] rounded-lg text-[12px] font-bold tabular-nums ${
                            when === 'planned'
                              ? 'border border-dashed border-hairline text-ink-2'
                              : pos === 'P'
                                ? 'bg-gold-soft text-gold-ink'
                                : sitting
                                  ? 'bg-canvas text-ink-2'
                                  : 'bg-raised text-ink'
                          } ${when === 'now' ? 'ring-2 ring-inset ring-ink' : ''}`}
                        >
                          {label(pos)}
                        </span>
                      </td>
                    );
                  })}
                  <td className="pl-2 pr-1 py-1 text-center text-[12.5px] font-semibold tabular-nums text-ink">{c?.field ?? 0}</td>
                  <td className="pl-1 pr-1 py-1 text-center text-[12.5px] font-semibold tabular-nums text-ink-2">{c?.bench ?? 0}</td>
                  {showPitches && (
                    <td className="pl-1 pr-3 py-1 text-center text-[12.5px] font-semibold tabular-nums text-ink">
                      {pitches > 0 ? pitches : ''}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="px-4 py-3 text-[12px] text-ink-2">
        Dashed innings are the plan. Change the game on the Defense tab.
      </p>
    </div>
  );
};
